/**
 * Acciones REDUX para la papelera de la aplicación.
 *
 * La colección `trash` actúa como soft-delete: antes de eliminar un
 * elemento (carpeta, archivo legacy o archivo inline) se guarda una
 * copia con la información mínima necesaria para restaurarlo.
 *
 * Estructura del documento `trash/{id}`:
 *   customerId            string       — id del cliente (carpeta raíz)
 *   itemType              'folder' | 'file-legacy' | 'file-inline'
 *   itemName              string       — nombre visible para listas
 *   itemExt               string?      — extensión (sólo archivos)
 *   breadcrumb            string[]     — ruta de nombres (display)
 *   deletedAt             Timestamp    — server timestamp
 *   deletedBy             string       — uid del usuario
 *   deletedByName         string?      — nombre del usuario
 *   requestedBy           string?      — uid de quien pidió el borrado (solo si salió de una
 *                                        solicitud aprobada; la supervisora figura en deletedBy)
 *   requestedByName       string?      — nombre del solicitante
 *   restorePayload        object       — datos para revertir el borrado
 *
 * `restorePayload` por tipo:
 *  - folder:
 *      parentRefPath:     string       — doc Firestore que contiene `folders[]`
 *      folderEntry:       object       — entry tal como se guardó en `folders[]`
 *      subCollectionId:   string       — id de la subcolección bajo el padre
 *      subCollectionDocs: { id, data }[]   — primer nivel (legacy/compat)
 *      descendants:       { path, data }[] — ÁRBOL COMPLETO (delete recursivo)
 *      legacyFiles:       { path, data }[] — docs en `files/` apuntados por refs
 *  - file-legacy:
 *      parentRefPath:     string       — doc contenedor (con `reference`)
 *      parentData:        object       — data del doc contenedor
 *      fileRefPath:       string       — doc real del archivo
 *      fileData:          object       — data del doc real del archivo
 *  - file-inline:
 *      parentRefPath:     string       — doc contenedor con `files[]`
 *      fileData:          object       — entry a re-insertar via arrayUnion
 */

import { TRASH } from '../../../application/types';
import { sanitizeForFirestore } from '../../lib/firestore_safe';
import { trashScope, mergeTrashItems, canRestoreTrashItem } from '../../lib/trash_scope';
import { planAttachmentRestore, ATTACHMENT_KINDS } from '../../lib/attachment_trash';
import { denormalizeFileFlagsToParent } from '../../lib/denormalize_file_flags';

const RETENTION_DAYS = 30;

/**
 * Auto-purga: elimina permanentemente los items cuyo deletedAt
 * exceda RETENTION_DAYS. Se ejecuta al leer la papelera (best-effort).
 *
 * @returns {Promise<number>} cantidad de items purgados
 */
const autoPurgeExpired = async (db) => {
  try {
    const snap = await db.collection('trash').get();
    const now = Date.now();
    const expired = snap.docs.filter(d => {
      const data = d.data();
      const retention = data.retentionDays || RETENTION_DAYS;
      const deletedAt = data.deletedAt?.toMillis?.();
      if (!deletedAt) return false;
      const ageDays = (now - deletedAt) / (1000 * 60 * 60 * 24);
      return ageDays > retention;
    });
    if (expired.length === 0) return 0;

    // Borrado uno por uno: cada doc puede traer sub-colección `chunks`
    // (formato del delete por job) que también hay que limpiar.
    for (const d of expired) await deleteTrashDocWithChunks(db, d.ref);
    return expired.length;
  } catch (e) {
    console.warn('Auto-purga papelera falló (continuando):', e?.message || e);
    return 0;
  }
};

/**
 * Borra un doc de `trash` junto con su sub-colección `chunks` (formato
 * chunked del delete por job). Para registros legacy (sin chunks) el get
 * de la sub-colección viene vacío y solo se borra el doc.
 */
const deleteTrashDocWithChunks = async (db, docRef) => {
  try {
    const chunksSnap = await docRef.collection('chunks').get();
    const CHUNK = 400;
    const refs = chunksSnap.docs.map(d => d.ref);
    for (let i = 0; i < refs.length; i += CHUNK) {
      const b = db.batch();
      refs.slice(i, i + CHUNK).forEach(r => b.delete(r));
      await b.commit();
    }
  } catch (e) {
    console.warn('No se pudieron borrar los chunks del trash:', e?.message);
  }
  await docRef.delete();
};

/**
 * ¿La ruta es una matriz de permisos? `folders/{cid}/…/users/{uid}` en
 * cualquier profundidad (empresa, permisos_carpetas o subcarpeta).
 */
const isMatrixPath = (path) => {
  if (typeof path !== 'string' || !path) return false;
  const parts = path.split('/');
  return parts[0] === 'folders'
    && parts.length >= 4
    && parts.length % 2 === 0
    && parts[parts.length - 2] === 'users';
};

/**
 * Escribe `items` ({ path, data }) en batches de `size`: primero los docs de
 * carpetas y archivos, después las matrices en batches APARTE (spec
 * 2026-10-06 Fase 6, ruling R10). Las reglas solo dejan escribir matrices a
 * quien administra la empresa; si rechazan un batch de matrices la carpeta
 * se restaura igual (hereda los permisos de arriba) y se cuentan las
 * matrices que no se pudieron restaurar para avisarle al usuario.
 * Un error en los docs de carpetas sí corta la restauración.
 *
 * @returns {Promise<number>} matrices que no se restauraron
 */
const writeRestoreItems = async (db, items, size) => {
  const valid = (items || []).filter(it => it?.path);
  const docs = valid.filter(it => !isMatrixPath(it.path));
  const matrices = valid.filter(it => isMatrixPath(it.path));
  for (let i = 0; i < docs.length; i += size) {
    const b = db.batch();
    docs.slice(i, i + size).forEach(d => b.set(db.doc(d.path), d.data || {}));
    await b.commit();
  }
  let skipped = 0;
  for (let i = 0; i < matrices.length; i += size) {
    const group = matrices.slice(i, i + size);
    try {
      const b = db.batch();
      group.forEach(d => b.set(db.doc(d.path), d.data || {}));
      await b.commit();
    } catch (e) {
      skipped += group.length;
      console.warn('Restaurar papelera: matrices rechazadas', e?.code || e?.message);
    }
  }
  return skipped;
};

const extractCustomerId = (path) => {
  // Las rutas siempre comienzan con `folders/{customerId}/...`
  if (!path) return null;
  const parts = path.split('/');
  const idx = parts.indexOf('folders');
  return idx >= 0 && parts.length > idx + 1 ? parts[idx + 1] : null;
};

const safeDisplayName = (state) => {
  try {
    const u = state?.()?.get('authenticate')?.get('login')?.user;
    return u?.displayName || u?.email || null;
  } catch (_e) { return null; }
};

/**
 * Helper interno usado por los thunks de delete para registrar
 * el item antes de su eliminación definitiva.
 *
 * @param {Function} firebase   — la función firebase del thunk middleware
 * @param {Function} state      — el getState del thunk middleware
 * @param {Object}   payload    — describe el item a archivar
 *   { itemType, itemName, itemExt?, customerId, breadcrumb?, restorePayload }
 * @returns DocumentReference del item creado en `trash`
 */
export const recordToTrash = async (firebase, state, payload) => {
  const db = firebase().firestore();
  const uid = firebase().auth().currentUser?.uid || null;
  const trashRef = db.collection('trash').doc();
  // Sanitizamos el restorePayload para eliminar cualquier campo con `__`
  // (rechazados por Firestore). El caso típico es `aiEmbedding.__type__`
  // del módulo IA que llega en `parentData`/`fileData` del snapshot.
  const safeRestorePayload = sanitizeForFirestore(payload.restorePayload || {});
  await trashRef.set({
    customerId: payload.customerId || null,
    itemType: payload.itemType,
    itemName: payload.itemName || '',
    itemExt: payload.itemExt || null,
    breadcrumb: payload.breadcrumb || [],
    deletedAt: firebase().firestore.FieldValue.serverTimestamp(),
    deletedBy: uid,
    deletedByName: safeDisplayName(state),
    restorePayload: safeRestorePayload,
    retentionDays: RETENTION_DAYS,
    ...(payload.requestedBy?.uid
      ? { requestedBy: payload.requestedBy.uid, requestedByName: payload.requestedBy.name || null }
      : {})
  });
  return trashRef;
};

/**
 * Quien solo SOLICITÓ el borrado (lo ejecutó una supervisora) ve la entrada
 * pero no la restaura ni la purga: lanza si el usuario no es del alcance
 * global ni quien la borró.
 */
const assertCanTouch = (firebase, roles, data, verbo) => {
  const uid = firebase().auth().currentUser?.uid || null;
  const scope = trashScope({ roles, uid });
  if (!canRestoreTrashItem({ item: data, uid, scope })) {
    throw new Error(`No puedes ${verbo} este elemento: lo eliminó otra persona al aprobar tu solicitud.`);
  }
};

/**
 * Acciones expuestas
 */
export const trash = {

  /**
   * Lista items en papelera. Acepta filtro por cliente.
   *   read({ roles, filter: { customerId?, from?, to? } })
   */
  read: ({ roles, filter = {} } = {}) => (dispatch, state, firebase) => {

    dispatch({ type: TRASH.READ.READING_TRASH });

    return new Promise(async (resolve, reject) => {
      try {
        const db = firebase().firestore();

        // Alcance de la papelera (lib/trash_scope.js):
        //  - Root (documents.root === true): ve TODA la papelera.
        //  - Cualquier otro, contadoras con level 'all' incluidas: solo lo que
        //    él borró. Antes level 'all' veía lo de todos y podía vaciarlo;
        //    se cerró a pedido de las supervisoras (reunión 2026-09-18).
        // Las rules de `trash` exigen lo mismo: una query global de un no-root
        // la rechaza el servidor con "Missing or insufficient permissions".
        const scope = trashScope({ roles, uid: firebase().auth().currentUser?.uid });
        if (!scope) {
          return resolve(dispatch({ type: TRASH.READ.READED_TRASH, items: [], autoPurged: 0 }));
        }

        // Auto-purga (borrado global de expirados) solo para root: es una
        // query global, y las rules ya no se la permiten a nadie más.
        const autoPurged = scope.global ? await autoPurgeExpired(db) : 0;

        let items;
        if (scope.global) {
          // Orden por servidor (sin where) — evita índices compuestos.
          const snap = await db.collection('trash').orderBy('deletedAt', 'desc').get();
          items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } else {
          // Lo propio (deletedBy) y lo borrado por una solicitud mía aprobada
          // (requestedBy; lo borró la supervisora). Dos equality simples, sin
          // orderBy → sin índice compuesto; cada una cumple la rule. Se unen
          // por id y se ordenan en memoria.
          // Si la de requestedBy falla (p. ej. reglas viejas aún sin
          // desplegar) se trata como vacía: lo propio se sigue viendo.
          const [mine, requested] = await Promise.allSettled([
            db.collection('trash').where('deletedBy', '==', scope.uid).get(),
            db.collection('trash').where('requestedBy', '==', scope.uid).get(),
          ]);
          if (mine.status === 'rejected') throw mine.reason;
          if (requested.status === 'rejected') {
            console.warn('Papelera: no se pudo leer lo solicitado por ti (continuando):', requested.reason?.message || requested.reason);
          }
          const toItems = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }));
          items = mergeTrashItems(toItems(mine.value), requested.status === 'fulfilled' ? toItems(requested.value) : []);
        }

        // Filtros locales — más simples y sin requerir índices Firestore
        if (filter.customerId) {
          items = items.filter(it => it.customerId === filter.customerId);
        }
        if (filter.from) {
          const from = new Date(filter.from).getTime();
          items = items.filter(it => it.deletedAt?.toMillis?.() >= from);
        }
        if (filter.to) {
          const to = new Date(filter.to).getTime();
          items = items.filter(it => it.deletedAt?.toMillis?.() <= to);
        }

        return resolve(dispatch({
          type: TRASH.READ.READED_TRASH,
          items,
          autoPurged
        }));
      } catch (error) {
        return reject(dispatch({
          type: TRASH.READ.ERROR,
          code: error.code,
          message: error.message
        }));
      }
    });
  },

  /**
   * Restaura un item desde la papelera a su ubicación original.
   *   restore({ id })
   */
  restore: ({ id, roles }) => (dispatch, state, firebase) => {

    dispatch({ type: TRASH.RESTORE.RESTORING_TRASH });

    return new Promise(async (resolve, reject) => {
      try {
        const db = firebase().firestore();
        const trashRef = db.collection('trash').doc(id);
        const trashSnap = await trashRef.get();
        if (!trashSnap.exists) throw new Error('El item de papelera ya no existe');
        const data = trashSnap.data();
        assertCanTouch(firebase, roles, data, 'restaurar');
        const fb = firebase();
        const rp = data.restorePayload || {};

        if (data.itemType === 'folder') {
          // Restaura entry en parent.folders[] y recrea TODA la jerarquía.
          // Si el payload incluye `descendants` (formato nuevo del delete
          // recursivo), recreamos cada doc en su path original — soporta
          // árboles arbitrariamente profundos.
          const parent = db.doc(rp.parentRefPath);
          const CHUNK = 400;
          let matricesSkipped = 0;

          // 1) Restaurar todos los descendientes (sub-folders y sus
          //    contenidos), si están disponibles.
          // Formato chunked (delete por job): los descendientes viven en
          // trash/{id}/chunks. Bloquear si el job aún está borrando.
          if (rp.descendantsChunked) {
            if (data.status === 'deleting') {
              throw new Error('La eliminación de esta carpeta aún está en curso. Intenta restaurar cuando termine.');
            }
            const chunksSnap = await trashRef.collection('chunks').get();
            const chunkItems = [];
            chunksSnap.docs.forEach(d => {
              (d.data().items || []).forEach(it => { if (it?.path) chunkItems.push(it); });
            });
            matricesSkipped += await writeRestoreItems(db, chunkItems, CHUNK);
          } else if (Array.isArray(rp.descendants) && rp.descendants.length > 0) {
            matricesSkipped += await writeRestoreItems(db, rp.descendants, CHUNK);
          } else if (Array.isArray(rp.subCollectionDocs) && rp.subCollectionId) {
            // Fallback al formato legacy (solo primer nivel).
            const sub = parent.collection(rp.subCollectionId);
            for (let i = 0; i < rp.subCollectionDocs.length; i += CHUNK) {
              const b = db.batch();
              rp.subCollectionDocs.slice(i, i + CHUNK).forEach(d => {
                b.set(sub.doc(d.id), d.data || {});
              });
              await b.commit();
            }
          }

          // 2) Restaurar archivos legacy (en colección `files/`)
          if (Array.isArray(rp.legacyFiles) && rp.legacyFiles.length > 0) {
            for (let i = 0; i < rp.legacyFiles.length; i += CHUNK) {
              const b = db.batch();
              rp.legacyFiles.slice(i, i + CHUNK).forEach(f => {
                if (f?.path) b.set(db.doc(f.path), f.data || {});
              });
              await b.commit();
            }
          }

          // 3) Restaurar la entry en parent.folders[] + borrar el registro
          //    (y sus chunks, si los tiene) de la papelera.
          const finalBatch = db.batch();
          finalBatch.update(parent, {
            folders: fb.firestore.FieldValue.arrayUnion(rp.folderEntry)
          });
          await finalBatch.commit();
          await deleteTrashDocWithChunks(db, trashRef);
          return resolve(dispatch({
            type: TRASH.RESTORE.RESTORED_TRASH,
            id,
            matricesSkipped
          }));
        } else if (data.itemType === 'file-legacy') {
          // Recrea el doc real y el wrapper que apuntaba a él.
          const fileRef = db.doc(rp.fileRefPath);
          const parent = db.doc(rp.parentRefPath);
          const batch = db.batch();
          batch.set(fileRef, rp.fileData || {});
          batch.set(parent, rp.parentData || {});
          batch.delete(trashRef);
          await batch.commit();
        } else if (data.itemType === 'file-inline') {
          // Re-inserta el entry en parent.files[]
          const parent = db.doc(rp.parentRefPath);
          const batch = db.batch();
          batch.update(parent, {
            files: fb.firestore.FieldValue.arrayUnion(rp.fileData)
          });
          batch.delete(trashRef);
          await batch.commit();
        } else if (Object.values(ATTACHMENT_KINDS).some(k => k.itemType === data.itemType)) {
          // Adjunto del cajón (contabilización, XML o vinculado). El plan se
          // calcula sobre el documento padre RELEÍDO ahora, no sobre la foto
          // del borrado: pudo cambiar entre medio (lib/attachment_trash.js).
          const parent = db.doc(rp.parentPath);
          const parentSnap = await parent.get();
          const plan = planAttachmentRestore({
            restorePayload: rp,
            parent: { exists: parentSnap.exists, data: parentSnap.exists ? parentSnap.data() : null },
            makeRef: (path) => db.doc(path),
          });
          if (plan.error) throw new Error(plan.error);

          const batch = db.batch();
          batch.set(db.doc(rp.attachmentPath), rp.attachmentData || {});
          batch.update(parent, { [plan.field]: plan.next });
          batch.delete(trashRef);
          await batch.commit();
          // Contabilización: el listado pinta el tag desde files[] del padre.
          if (plan.denormalize) await denormalizeFileFlagsToParent(parent, firebase);
        } else {
          throw new Error(`Tipo no soportado: ${data.itemType}`);
        }

        return resolve(dispatch({
          type: TRASH.RESTORE.RESTORED_TRASH,
          id,
          matricesSkipped: 0
        }));
      } catch (error) {
        return reject(dispatch({
          type: TRASH.RESTORE.ERROR,
          code: error.code,
          message: error.message
        }));
      }
    });
  },

  /**
   * Elimina permanentemente un item de la papelera.
   *   purge({ id })
   */
  purge: ({ id, roles }) => (dispatch, state, firebase) => {

    dispatch({ type: TRASH.PURGE.PURGING_TRASH });

    return new Promise(async (resolve, reject) => {
      try {
        const db = firebase().firestore();
        const snap = await db.collection('trash').doc(id).get();
        if (snap.exists) assertCanTouch(firebase, roles, snap.data(), 'eliminar definitivamente');
        await deleteTrashDocWithChunks(db, db.collection('trash').doc(id));
        return resolve(dispatch({
          type: TRASH.PURGE.PURGED_TRASH,
          id
        }));
      } catch (error) {
        return reject(dispatch({
          type: TRASH.PURGE.ERROR,
          code: error.code,
          message: error.message
        }));
      }
    });
  },

  /**
   * Elimina permanentemente los items de la papelera que el usuario puede
   * ver. Si se pasa customerId, sólo los de ese cliente.
   *
   * Alcance (lib/trash_scope.js): root vacía la papelera global; cualquier
   * otro vacía SOLO lo que él borró. Antes cualquier level 'all' borraba de
   * forma permanente la papelera de todo el sistema con un clic.
   */
  purgeAll: ({ roles, customerId } = {}) => (dispatch, state, firebase) => {

    dispatch({ type: TRASH.PURGE_ALL.PURGING_ALL_TRASH });

    return new Promise(async (resolve, reject) => {
      try {
        const db = firebase().firestore();
        const scope = trashScope({ roles, uid: firebase().auth().currentUser?.uid });
        if (!scope) {
          return resolve(dispatch({ type: TRASH.PURGE_ALL.PURGED_ALL_TRASH, count: 0 }));
        }

        let docs;
        if (scope.global) {
          let query = db.collection('trash');
          if (customerId) query = query.where('customerId', '==', customerId);
          docs = (await query.get()).docs;
        } else {
          // Solo lo que ÉL borró (deletedBy == uid): lo que apenas solicitó y
          // borró una supervisora no se incluye. El cliente se filtra en
          // memoria, igual que en read.
          docs = (await db.collection('trash').where('deletedBy', '==', scope.uid).get()).docs;
          if (customerId) docs = docs.filter(d => d.data().customerId === customerId);
        }

        // Uno por uno: cada doc puede traer sub-colección `chunks` a limpiar.
        for (const d of docs) await deleteTrashDocWithChunks(db, d.ref);
        return resolve(dispatch({
          type: TRASH.PURGE_ALL.PURGED_ALL_TRASH,
          count: docs.length
        }));
      } catch (error) {
        return reject(dispatch({
          type: TRASH.PURGE_ALL.ERROR,
          code: error.code,
          message: error.message
        }));
      }
    });
  }
};

export { extractCustomerId, isMatrixPath };
